import ReactJson from "@microlink/react-json-view";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";

import { Message } from "@/shared/reader";

import { RenderingError } from "../RenderingError";

import styles from "./FormattedMessage.module.css";
import { sanitizeForJsonView } from "./sanitizeForJsonView";

interface FormattedMessageProps {
  error: Error | null;
  message: Message | null;
}

export function FormattedMessage({ error, message }: FormattedMessageProps) {
  // useTheme is deprecated for styling in this codebase,
  // but ReactJson is a third-party component that takes its theme as a string prop,
  // so we need the resolved palette mode ("light" or "dark") to map onto its theme name.
  const theme = useTheme();

  const sanitized = useMemo(
    () => (message !== null ? sanitizeForJsonView(message) : null),
    [message],
  );

  if (error !== null) {
    return <RenderingError error={error} />;
  }

  // Keep ReactJson mounted even when there is no message to render, hiding it via `display: none` below.
  // ReactJson holds which nodes the user has expanded in internal state that does not survive an unmount,
  // so unmounting between timestamp changes would collapse the tree on every
  // tick and make the panel unusable as the playhead moves.
  const noMessage = message === null || sanitized === null;

  return (
    <div
      className={`ph-no-capture ${styles.message}`}
      style={{ display: noMessage ? "none" : undefined }}
    >
      <ReactJson
        src={sanitized ?? {}}
        name={false}
        theme={theme.palette.mode === "dark" ? "monokai" : "rjv-default"}
        collapsed={2}
        collapseStringsAfterLength={80}
        displayDataTypes={false}
        enableClipboard={true}
        displayObjectSize={true}
        style={{ backgroundColor: "transparent" }}
      />
    </div>
  );
}
